import {
  ReportModalConfig,
  ReportModalSectionField
} from '@features/siteStatusV2/components/reports/types/commun';
import { ReportStatus } from '@features/siteStatusV2/constants';
import { convertDateFormat } from '@features/siteStatusV2/utils/helpers';
import theme from '@theme';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Clock } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { TranslationNamespaces } from '../../../../../locales/constants/translationNamespaces.enum';
import {
  BodyContainer,
  DisplayFieldName,
  DisplayFieldValue,
  GridContainer,
  SubTitle
} from '../../display-form';
import { Icon } from '../../icon';
import { MapStatusToColor, MapStatusToDisplay } from '../maps';
import ReportTag from '../ReportTag';

interface InformationsColumn {
  report: ReportModalConfig;
  onHide: () => void;
}

const InformationsColumn: React.FC<InformationsColumn> = ({ report, onHide }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const statusColor = MapStatusToColor[report.header.status];
  const statusDisplay =
    report.header.status === ReportStatus.NO_STATUS
      ? report.header.rawStatus
      : t(MapStatusToDisplay[report.header.status]);

  const formatTypeValues = ({ value, badge, type }: ReportModalSectionField) => {
    switch (type) {
      case 'date':
        return (
          <DisplayFieldValue label={convertDateFormat(value?.toString(), true)} badge={badge} />
        );
      case 'work-order':
        return <WorkOrderStatusText>{value}</WorkOrderStatusText>;
      default:
        return <DisplayFieldValue label={value?.toString()} badge={badge} />;
    }
  };

  return (
    <Col xs={7}>
      <Row>
        <Col className="p-0">
          <CustomBodyContainer>
            <HeaderCol>
              <Icon iconName="chevron-double-left" className="icon" onClick={onHide} />
              <HeaderColTitle>{report.header.title}</HeaderColTitle>
            </HeaderCol>
            <SubHeaderCol>
              <TicketRef className="id-ticket">{report.header.ticketId}</TicketRef>
              <ReportTag color={statusColor} full={true} label={statusDisplay ?? ''} />
            </SubHeaderCol>
            <Tag>
              <Clock fill={theme.palette.grey[400]} width={8} height={8} />
              <CreationDate>
                {`${t('report.creationDate')} ${convertDateFormat(
                  report.header.creationDate,
                  true
                )}`}
              </CreationDate>
            </Tag>
          </CustomBodyContainer>
        </Col>
      </Row>
      {report.sections.map((section) => (
        <BodyContainer key={section.title}>
          <SubTitle color="blue" display={section.title} />
          {section.fields !== undefined ? (
            <GridContainer>
              {section.fields?.map((field) => (
                <React.Fragment key={field.label}>
                  <DisplayFieldName iconName={field.icon}>{field.label}</DisplayFieldName>
                  {formatTypeValues(field)}
                </React.Fragment>
              ))}
            </GridContainer>
          ) : (
            section.custom
          )}
        </BodyContainer>
      ))}
    </Col>
  );
};

const Tag = styled.div`
  display: flex;
  align-items: center;
  max-width: fit-content;
  background-color: ${theme.palette.grey[100]};
  padding: 0 0.25rem 0 0.375rem;
  border-radius: 4px;
`;

const CreationDate = styled.div`
  font-size: 0.625rem;
  line-height: 1rem;
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.grey[400]};
  margin-left: 0.25rem;
`;

const CustomBodyContainer = styled(BodyContainer)`
  padding-left: 2.75rem;
  padding-right: 1rem;
  position: relative;
  background: linear-gradient(180deg, #e7f0ff 0%, rgba(255, 255, 255, 0) 100%);
`;

const HeaderColTitle = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.blue[500]};
`;

const HeaderCol = styled.div`
  display: flex;
  align-items: center;

  .icon {
    stroke: ${theme.palette.grey[400]};
    position: absolute;
    top: 11px;
    left: 15px;
    cursor: pointer;
  }
`;

const SubHeaderCol = styled.div`
  display: flex;
  margin: 0.25rem 0;
`;

const WorkOrderStatusText = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.regular};
  color: ${theme.palette.blue[600]};
  padding: 0.125rem 0.5rem;
  border: solid 1px ${theme.palette.blue[600]};
  line-height: 1;
  border-radius: 4px;
`;

const TicketRef = styled.div`
  font-size: ${theme.fonts.size.xxs};
  font-weight: ${theme.fonts.weight.regular};
  color: ${theme.palette.grey[500]};
  padding-right: 0.85rem;
  margin-right: 0.85rem;
  position: relative;

  &::before {
    content: '';
    display: inline-block;
    right: 0;
    top: 50%;
    width: 6px;
    height: 6px;
    transform: translate(50%, -50%);
    position: absolute;
    background: ${theme.palette.grey[300]};
    border-radius: 6px;
    }
  }
`;

export default InformationsColumn;
